import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

type SeoAsset = {
  slug: string;
  path: string;
  title: string;
  description: string;
  category: string;
  image?: string;
  keywords: string[];
};

const ToolLanding = () => {
  const { slug = "" } = useParams();
  const [asset, setAsset] = useState<SeoAsset | null>(null);
  const [isLoaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch("/seo-assets.json")
      .then((response) => response.json())
      .then((assets: SeoAsset[]) => {
        setAsset(assets.find((item) => item.slug === slug) || null);
      })
      .catch(() => setAsset(null))
      .finally(() => setLoaded(true));
  }, [slug]);

  if (!isLoaded) {
    return (
      <main className='border-t-2'>
        <div className='mx-auto max-w-[900px] px-6 py-10 xs:px-4'>
          <p className='text-sm leading-sm text-main-grey-800'>Loading asset...</p>
        </div>
      </main>
    );
  }

  if (!asset) {
    return (
      <main className='border-t-2'>
        <div className='mx-auto max-w-[900px] px-6 py-10 xs:px-4'>
          <h1 className='text-3xl leading-3xl font-bold text-black md:text-2xl md:leading-2xl'>
            Procreate asset not found
          </h1>
          <p className='mt-3 text-sm leading-sm text-main-grey-800'>
            Browse the full Tools for Procreate library to find brush sets, fonts, color palettes, stickers, and 3D models.
          </p>
          <Link className='mt-5 inline-flex rounded-[12px] bg-main-blue px-5 py-3 text-sm leading-sm font-bold text-white' to='/'>
            Browse Procreate brushes
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className='border-t-2'>
      <div className='mx-auto grid max-w-[1040px] grid-cols-[minmax(0,1fr)_320px] gap-8 px-6 py-10 md:grid-cols-1 xs:px-4'>
        <section>
          <p className='mb-2 text-xxs leading-xxs font-bold uppercase text-main-grey-800'>
            {asset.category}
          </p>
          <h1 className='text-3xl leading-3xl font-bold text-black md:text-2xl md:leading-2xl'>
            {asset.title}
          </h1>
          <p className='mt-3 text-sm leading-sm text-main-grey-800'>{asset.description}</p>
          <div className='mt-6 flex flex-wrap gap-2'>
            {asset.keywords.map((keyword) => (
              <span key={keyword} className='rounded-full bg-main-grey-200 px-3 py-2 text-xs leading-xs text-black'>
                {keyword}
              </span>
            ))}
          </div>
          <Link className='mt-8 inline-flex rounded-[12px] bg-main-blue px-5 py-3 text-sm leading-sm font-bold text-white' to='/'>
            Open Tools for Procreate
          </Link>
        </section>
        {asset.image && (
          <img
            src={asset.image}
            alt={asset.title}
            className='aspect-square w-full rounded-[12px] object-cover'
            loading='eager'
          />
        )}
      </div>
    </main>
  );
};

export { ToolLanding };
